import React from "react";
import { Link } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";

const doctorFaqs = [
  {
    question: "How will patient consultations be assigned to me through the platform?",
    answer:
      "Patient consultations are assigned based on your specialty, availability, consultation mode, and patient requirements. New consultation requests will be visible in your dashboard.",
  },
  {
    question: "How can I manage and track follow-up consultations for my patients?",
    answer:
      "You can schedule, view, and manage follow-up consultations directly from the patient's profile. The platform maintains a record of previous consultations and upcoming follow-ups.",
  },
  {
    question: "What should I do if the medicine I wish to prescribe is not available on the platform?",
    answer:
      "You may prescribe the medicine manually and notify the support team. The platform team will review availability and suggest suitable alternatives when applicable.",
  },
  {
    question: "How can I access previous consultation records and treatment history of a patient?",
    answer:
      "All consultation notes, prescriptions, reports, and treatment histories are securely stored within the patient's profile and can be accessed during future consultations.",
  },
  {
    question: "How will I know whether a patient has purchased the prescribed medicines?",
    answer:
      "The platform provides prescription fulfillment updates, allowing you to track whether prescribed medicines have been ordered through the integrated pharmacy services.",
  },
  {
    question: "Can I modify or update a prescription after the consultation is completed?",
    answer:
      "Yes. Prescription updates can be made through the patient's consultation record. The revised prescription will be available to the patient immediately after approval.",
  },
  {
    question: "How can I recommend therapies, Panchakarma procedures, or wellness programs to a patient?",
    answer:
      "During consultation, you can add therapy recommendations, Panchakarma procedures, Yoga practices, dietary advice, and wellness programs directly to the patient's treatment plan.",
  },
  {
    question: "What happens if a patient misses a scheduled consultation or follow-up appointment?",
    answer:
      "Missed appointments are recorded in the system, and the patient may receive reminders to reschedule. Doctors can also initiate a follow-up request when necessary.",
  },
  {
    question: "How can I monitor patient progress and treatment outcomes through the platform?",
    answer:
      "Patient progress can be monitored through follow-up records, uploaded reports, symptom assessments, treatment notes, and wellness tracking tools available in the dashboard.",
  },
  {
    question: "How are referrals handled when a patient requires consultation from another specialist or department?",
    answer:
      "Doctors can initiate referrals through the platform. The patient's records, consultation notes, and treatment history are securely shared with the referred specialist for continuity of care.",
  },
];

const patientFaqs = [
  {
    question: "How do I book a consultation with a doctor through the platform?",
    answer:
      "Patients can search for doctors based on specialty, location, or availability and book appointments directly through the platform.",
  },
  {
    question: "How can I access my previous consultation records and prescriptions?",
    answer:
      "All consultation records, prescriptions, and treatment plans are securely stored in your patient profile and can be accessed anytime.",
  },
  {
    question: "How do I upload my medical reports for the doctor's review?",
    answer:
      "Patients can upload laboratory reports, scans, and other medical documents through the Reports section before or after a consultation.",
  },
  {
    question: "Can I schedule follow-up consultations with the same doctor?",
    answer:
      "Yes. Follow-up consultations can be booked directly from your consultation history or through the doctor's profile.",
  },
  {
    question: "How will I receive my prescribed medicines and wellness products?",
    answer:
      "Prescribed medicines and recommended wellness products can be ordered through the platform and delivered based on service availability.",
  },
  {
    question: "Can I receive personalized diet, Yoga, and lifestyle recommendations?",
    answer:
      "Yes. Doctors can provide customized diet plans, Yoga practices, lifestyle guidance, and wellness recommendations as part of your treatment plan.",
  },
  {
    question: "How can I enroll in Panchakarma therapies or wellness programs?",
    answer:
      "Recommended therapies and wellness programs can be viewed in your treatment plan, and appointments can be scheduled through the platform.",
  },
  {
    question: "What should I do if I miss my scheduled consultation?",
    answer:
      "You can reschedule your appointment through the platform or contact support for assistance based on the appointment policy.",
  },
  {
    question: "How can I track my treatment progress and health improvements?",
    answer:
      "Patients can monitor consultation history, follow-up records, uploaded reports, and treatment recommendations through their dashboard.",
  },
  {
    question: "How can I consult another specialist if required?",
    answer:
      "If a referral is recommended, you can book an appointment with the referred specialist, and your medical records will be available for seamless continuity of care.",
  },
];

const styles = {
  breadcrumb: {
    padding: "18px 0",
    background: "#f8fafc",
    borderBottom: "1px solid #eef1f6",
  },
  pageTitle: {
    fontSize: "clamp(2remfont-weight: 500;color: rgb(15, 23, 42);outline: none;box-shadow: none;background-color: transparent;background-image: none;border-width: medium;border-style: none;border-color: currentcolor;border-image: none;border-radius: 0px;padding: 1rem 1.25rem;min-height: auto;transition: background-color 0.25s, box-shadow 0.25s;, 2vw, 3rem)",
    fontWeight: 500,
    color: "#0f172a",
    marginBottom: "8px",
  },
  pageDesc: {
    color: "#475569",
    fontSize: "1rem",
    lineHeight: 1.8,
    maxWidth: "760px",
  },
  section: {
    padding: "70px 0",
  },
  column: {
    marginBottom: "32px",
  },
  sectionHeading: {
    fontSize: "1.5rem",
    fontWeight: 500,
    color: "#0f172a",
    marginBottom: "18px",
  },
  accordionItem: {
    border: "1px solid #eef1f6",
    borderRadius: "18px",
    overflow: "hidden",
    background: "#fff",
    marginBottom: "16px",
  },
  accordionButton: {
    fontWeight: 500,
    color: "#0f172a",
    outline: "none",
    boxShadow: "none",
    backgroundColor: "transparent",
    backgroundImage: "none",
    border: "none",
    borderRadius: "0",
    padding: "1rem 1.25rem",
    minHeight: "auto",
    transition: "background-color 0.25s ease, box-shadow 0.25s ease",
  },
  accordionBody: {
    padding: "1rem 1.25rem",
    backgroundColor: "transparent",
  },
  card: {
    background: "#fff",
    borderRadius: "20px",
    padding: "26px 24px",
    border: "1px solid #eef1f6",
    boxShadow: "0 10px 35px rgba(15,23,42,0.08)",
    marginBottom: "18px",
  },
  question: {
    fontSize: "1rem",
    fontWeight: 700,
    marginBottom: "10px",
    color: "#0f172a",
  },
  answer: {
    fontSize: "0.96rem",
    color: "#475569",
    lineHeight: 1.75,
  },
  highlightLink: {
    color: "#1E7A8C",
    textDecoration: "none",
    fontWeight: 700,
  },
};

export default function Faq() {
  return (
    <>
      <Header />
      <style>{`
        .faq-accordions .accordion-button {
          transition: background-color 0.25s ease, color 0.25s ease, transform 0.25s ease;
        }

        .faq-accordions .accordion-button.collapsed {
          background-color: transparent;
        }

        .faq-accordions .accordion-button:not(.collapsed) {
          background-color: rgba(30,122,140,0.12);
          color: #0f172a;
          transform: translateY(-1px);
        }

        .faq-accordions .accordion-item {
          transition: transform 0.25s ease, box-shadow 0.25s ease, background-color 0.25s ease;
        }

        .faq-accordions .accordion-body {
          background-color: transparent;
        }

        .faq-accordions .accordion-collapse {
          transition: height 0.25s ease;
        }
      `}</style>
      <section style={styles.breadcrumb}>
        <div className="container">
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb mb-0">
              <li className="breadcrumb-item"><Link to="/">Home</Link></li>
              <li className="breadcrumb-item active" aria-current="page">FAQ</li>
            </ol>
          </nav>
        </div>
      </section>

      <section style={styles.section}>
        <div className="container">
          <div className="row justify-content-center text-center mb-5">
            <div className="col-12 col-xl-10">
              <div style={{ width: "64px", height: "4px", background: "linear-gradient(90deg, #58A186, #C38635)", margin: "0 auto 18px", borderRadius: "4px" }}></div>
              <h1 style={styles.pageTitle}>FAQs for Shree Varma Wellness</h1>
              <p style={{ ...styles.pageDesc, margin: "0 auto", textAlign: "center" }}>
                Find answers for doctors and patients, all in one place. These FAQs explain how consultation management, prescriptions, referrals, and wellness treatments work on the platform.
              </p>
            </div>
          </div>

          <div className="row gx-4 gy-4 justify-content-center align-items-start">
            <div className="col-12 col-lg-12" style={styles.column}>
              <div style={styles.sectionHeading}>Doctor FAQs</div>
              <div className="accordion faq-accordions" id="doctorFaqAccordion">
                {doctorFaqs.map((item, index) => (
                  <div className="accordion-item" key={`doctor-${index}`} style={styles.accordionItem}>
                    <h2 className="accordion-header" id={`doctor-heading-${index}`}>
                      <button
                        className="accordion-button collapsed"
                        type="button"
                        data-bs-toggle="collapse"
                        data-bs-target={`#doctor-collapse-${index}`}
                        aria-expanded="false"
                        aria-controls={`doctor-collapse-${index}`}
                        style={styles.accordionButton}
                      >
                        {item.question}
                      </button>
                    </h2>
                    <div
                      id={`doctor-collapse-${index}`}
                      className="accordion-collapse collapse"
                      aria-labelledby={`doctor-heading-${index}`}
                      data-bs-parent="#doctorFaqAccordion"
                    >
                      <div className="accordion-body" style={{ ...styles.answer, ...styles.accordionBody }}>
                        {item.answer}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="col-12 col-lg-12" style={styles.column}>
              <div style={styles.sectionHeading}>Patient FAQs</div>
              <div className="accordion faq-accordions" id="patientFaqAccordion">
                {patientFaqs.map((item, index) => (
                  <div className="accordion-item" key={`patient-${index}`} style={styles.accordionItem}>
                    <h2 className="accordion-header" id={`patient-heading-${index}`}>
                      <button
                        className="accordion-button collapsed"
                        type="button"
                        data-bs-toggle="collapse"
                        data-bs-target={`#patient-collapse-${index}`}
                        aria-expanded="false"
                        aria-controls={`patient-collapse-${index}`}
                        style={styles.accordionButton}
                      >
                        {item.question}
                      </button>
                    </h2>
                    <div
                      id={`patient-collapse-${index}`}
                      className="accordion-collapse collapse"
                      aria-labelledby={`patient-heading-${index}`}
                      data-bs-parent="#patientFaqAccordion"
                    >
                      <div className="accordion-body" style={{ ...styles.answer, ...styles.accordionBody }}>
                        {item.answer}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}
